import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Pagination,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  TablePagination,
} from '@mui/material';
import {
  EditOutlined as EditOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  RestoreOutlined as RestoreOutlinedIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import {
  hasPemissions,
  usarFuncionesComposicion,
  corregirTexto,
  firstLetterUpperCase,
} from '../../../../commons/utils';
import {
  getSurveysByPagination,
  getSurveysStore,
  getSurveysBySearch,
  storeDeleteSurvey,
  storeActiveSurvey,
} from '../../../../store/ratings/thunks';
import { useTableState } from '../../../../hooks/useTableState';
import { SinDatos } from '../../../../commons/generics/SinDatos';
import { GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import { StatusChip } from '../../../../commons/generics/StatusChip';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../commons/generics/StyledTableComponents';
import { ClampedText } from '../../../../commons/generics/ClampedText';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';

const headCells: HeadCell[] = [
  { id: 'index', label: '#', sortable: false },
  { id: 'name', label: 'Nombre', sortable: true },
  { id: 'areasRating.name', label: 'Área', sortable: false },
  { id: 'questions', label: 'Preguntas', sortable: false },
  { id: 'status', label: 'Estado', align: 'center', sortable: false },
  { id: 'actions', label: 'Acciones', align: 'center', sortable: false },
];

interface Props {
  onEdit: (survey: any) => void;
  onView: (survey: any) => void;
}

export const TableRatingsSurveys = ({ onEdit, onView }: Props) => {
  const dispatch = useDispatch();
  const { surveys_list }: any = useSelector((state: any) => state.ratingsDevices);

  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    openElem,
    setOpenElem,
    checked,
    searchTerm,
    ResponseComponents,
    handleResponse,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSwitchChange,
    updatePaginationFromList,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    changePaginationPage: changePaginationPageHook,
    order,
    orderBy,
    page,
    rowsPerPage,
    pagePagination,
    itemCountPagination,
    handleRequestSort,
    getSortedAndFilteredList,
    isServerSide,
    setSearchTerm,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
  } = useTableState('showInactivesSurveys', '');

  const [inputValue, setInputValue] = useState('');
  const [hasPermission, setHasPermission] = useState<boolean>(true);

  useEffect(() => {
    updatePaginationFromList(surveys_list);
  }, [surveys_list]);

  useEffect(() => {
    checkPermissions();
    getInitialData();
  }, []);

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.configurar.encuestas',
      'view'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.configurar.encuestas',
      'edit'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.configurar.encuestas',
      'delete'
    );
    if (del) setCanDelete(true);

    const rest = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.configurar.encuestas',
      'restore'
    );
    if (rest) setCanRestore(true);
  };

  const getInitialData = async (currentStatus?: number) => {
    const status = currentStatus !== undefined ? currentStatus : checked ? 0 : 1;
    const response = await dispatch(getSurveysStore(status));
    await handleResponse(response, 'obtener encuestas', false);
  };

  const handleDelete = (survey: any) => {
    setOpenElem(survey);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (survey: any) => {
    setOpenElem(survey);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDeleteSurvey(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar encuesta', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeActiveSurvey(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar encuesta', true);
  };

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getSurveysBySearch(p, checked ? 0 : 1),
      getInitialData,
      null,
      'buscar encuestas'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;
    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getSurveysBySearch(p, status),
        () => getInitialData(status),
        null,
        'buscar encuestas'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData());
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(getSurveysByPagination(value, checked ? 0 : 1) as any);
    await handleResponse(response, 'listar encuestas', false);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">No tienes permisos para ver este contenido.</Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'description'],
    'status',
    1
  );

  return (
    <Box sx={{ width: '100%', mt: 5 }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar encuesta..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas (Encuestas)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headCells={headCells}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={6} align="center">
                    <SinDatos />
                  </StyledTableCell>
                </TableRow>
              ) : (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any, index: number) => (
                    <StyledTableRow key={row.id} hover>
                      <StyledTableCell>
                        <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {(isServerSide
                            ? (pagePagination - 1) * rowsPerPage
                            : page * rowsPerPage) +
                            index +
                            1}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.name
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.areasRating?.name ? (
                          <ClampedText
                            text={usarFuncionesComposicion(
                              corregirTexto,
                              firstLetterUpperCase,
                              row.areasRating?.name
                            )}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Sin área"
                            sx={{ color: '#4c64ffff', background: '#FFE2E3', fontWeight: 700 }}
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={`${row.questions?.length || 0} Preguntas`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <StatusChip status={row.status} />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && onView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{
                              cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status === 1 && onEdit(row)}
                              disabled={!canEdit || row.status !== 1}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status === 1 ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canDelete && handleDelete(row)}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canRestore && handleRestore(row)}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>

          {dataTableList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataTableList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
            />
          )}

          {dataTableList.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
                padding: 2.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Pagination
                sx={{ justifyContent: 'center' }}
                color="primary"
                count={itemCountPagination}
                page={pagePagination}
                onChange={changePaginationPage}
                showFirstButton
                showLastButton
                shape="circular"
              />
            </Box>
          )}
        </TableContainer>
      </Card>

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
